import citiesJson from "./data/cities.json";
import type { Challenge, City, Language, Mode, SetId } from "./types";

export const cities = citiesJson as City[];

const subsetAliases: Record<SetId, (city: City, language: Language) => boolean> = {
  l1: city => city.regions.includes("l1"),
  st: (city, language) => city.regions.includes("sp") && !city.regions.includes(language),
  al: () => true,
  ct: city => city.regions.includes("ct"),
  eu: city => city.regions.includes("eu"),
  aa: city => city.regions.includes("aa"),
  na: city => city.regions.includes("na"),
  en: city => city.regions.includes("en"),
  ru: city => city.regions.includes("ru"),
  es: city => city.regions.includes("es"),
  pt: city => city.regions.includes("pt"),
};

export function citiesForSet(set: SetId, language: Language): City[] {
  return cities.filter(city => subsetAliases[set](city, language));
}

export function defaultSet(mode: Mode): SetId {
  return mode === 0 ? "al" : mode === 1 ? "l1" : "st";
}

export function mulberry32(seed: number): () => number {
  return () => {
    seed |= 0;
    seed = seed + 0x6d2b79f5 | 0;
    let value = Math.imul(seed ^ seed >>> 15, 1 | seed);
    value = value + Math.imul(value ^ value >>> 7, 61 | value) ^ value;
    return ((value ^ value >>> 14) >>> 0) / 4294967296;
  };
}

export function sample<T>(items: T[], count: number, seed: number): T[] {
  const copy = [...items];
  const random = mulberry32(seed);
  for (let index = copy.length - 1; index > 0; index--) {
    const swap = Math.floor(random() * (index + 1));
    [copy[index], copy[swap]] = [copy[swap], copy[index]];
  }
  return copy.slice(0, count);
}

export function createChallenge(mode: Mode, set: SetId, seed = crypto.getRandomValues(new Uint32Array(1))[0]): Challenge {
  return { version: 1, mode, set, seed };
}

export function encodeChallenge(challenge: Challenge): string {
  return btoa(JSON.stringify(challenge)).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
}

export function decodeChallenge(encoded: string | null): Challenge | null {
  if (!encoded) return null;
  try {
    const base64 = encoded.replaceAll("-", "+").replaceAll("_", "/");
    const value = JSON.parse(atob(base64)) as Partial<Challenge>;
    if (value.version !== 1 || ![0, 1, 2].includes(value.mode as number) || typeof value.seed !== "number") return null;
    if (!Object.hasOwn(subsetAliases, value.set ?? "")) return null;
    return value as Challenge;
  } catch {
    return null;
  }
}

export function scoreForAttempt(attempt: number): number {
  return [1000, 650, 350][attempt - 1] ?? 0;
}

export function setFromLegacyPath(pathname: string): { mode?: Mode; language?: Language } {
  const segments = pathname.split("/").filter(Boolean);
  const mode = ["0", "1", "2"].includes(segments[0]) ? Number(segments[0]) as Mode : undefined;
  const languageSegment = mode === undefined ? segments[0] : segments[1];
  const language = ["en", "ru", "es", "pt"].includes(languageSegment) ? languageSegment as Language : undefined;
  return { mode, language };
}
